import { useState, useRef, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { Colors, Spacings } from 'styles';
import {
  StepConfigButton,
  STEP_CHIP_INDEX_ATTR,
} from '../../../../HangingProtocol/Steps/StepConfigButton';
import {
  AddStepButton,
  AddStepButtonVariant,
  ADD_STEP_BUTTON_ATTR,
} from '../../../../HangingProtocol/Steps/AddStepButton';
import { isStepNameDuplicate } from '../../../../HangingProtocol/hpStepController';
import { generateTempStepId } from '../../../../HangingProtocol/tempStepId';
import type { StepConfiguration } from '../../../../HangingProtocol/steppedHPTypes';

import {
  MAX_HP_STEPS,
  type UseHPStepOperationsReturn,
} from '../../../../HangingProtocol/hooks/useHPStepOperations';
import {
  findClosestDragTarget,
  DragOrientation,
  DRAG_TARGET_SELECTOR,
} from '../../../../HangingProtocol/Steps/dragDropUtils';
import { HPDivider } from './hpStyles';

const CollapsibleHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${Spacings.small}rem 0;
  background: none;
  border: none;
  cursor: pointer;
  color: ${Colors.gray10};
`;

const CollapsibleTitle = styled.span`
  font-weight: 500;
  color: ${Colors.gray10};
  font-size: 16px;
  line-height: 24px;
  margin-bottom: 5px !important;
`;

const ChevronWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
    color: ${Colors.gray7};
  }
`;

const StepsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.small}rem;
  padding-top: ${Spacings.small}rem;
`;

type StepsConfigSectionProps = Pick<
  UseHPStepOperationsReturn,
  | 'steps'
  | 'currentStepIndex'
  | 'renamingStepId'
  | 'draggedIndex'
  | 'dropTarget'
  | 'handleStartRename'
  | 'deleteStep'
  | 'handleRenameConfirm'
  | 'handleRenameCancel'
  | 'handleStepBarDragStart'
  | 'handleStepBarDragOver'
  | 'handleStepBarDragEnd'
  | 'handleStepBarDrop'
  | 'addStep'
> & {
  /** When true the section starts expanded on mount (e.g. multi-step HPs). */
  defaultExpanded?: boolean;
  onStepClick: (index: number) => void;
  /** Disables the Add Step button when the form is submitting */
  isSubmitting?: boolean;
  /** Reports whether any step chip currently has a duplicate-name error. */
  onDuplicateError?: (hasDuplicate: boolean) => void;
};

/** Returns a validation error for a proposed step name, or null when it is valid. */
function getStepNameError(name: string, steps: StepConfiguration[], stepId: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return null;
  return isStepNameDuplicate(trimmed, steps, stepId) ? 'Duplicate label. Rename to continue' : null;
}

/** True when a non-empty proposed step name collides with another step's name. */
function isProposedNameDuplicate(
  name: string,
  steps: StepConfiguration[],
  stepId: string
): boolean {
  const trimmed = name.trim();
  return trimmed.length > 0 && isStepNameDuplicate(trimmed, steps, stepId);
}

export const StepsConfigSection = ({
  steps,
  currentStepIndex,
  renamingStepId,
  draggedIndex,
  dropTarget,
  defaultExpanded = false,
  onStepClick,
  handleStartRename,
  deleteStep,
  handleRenameConfirm,
  handleRenameCancel,
  handleStepBarDragStart,
  handleStepBarDragOver,
  handleStepBarDragEnd,
  handleStepBarDrop,
  addStep,
  isSubmitting,
  onDuplicateError,
}: StepsConfigSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const stepsSectionRef = useRef<HTMLDivElement>(null);
  const hasAutoExpandedRef = useRef(false);
  // Set once the user works the toggle themselves, so an arriving step never re-opens a section
  // they deliberately closed.
  const hasUserToggledRef = useRef(false);
  // Whether the list was already multi-step when the section mounted. Such a section is opened by the caller through
  // `defaultExpanded`, so auto-expansion has no work to do and must not override an explicit `defaultExpanded={false}`.
  const hadMultipleStepsOnMountRef = useRef((steps?.length ?? 0) > 1);

  // Expand once the step list *grows* past a single step, unless the user has already made that choice themselves.
  // Keying on that transition rather than on "steps were null at mount" covers both ways the form arrives at a
  // multi-step list late: steps still unresolved at mount (null), and a single stale step from an earlier form session
  // that the real list replaces. Gating on null-at-mount missed the second case and left it collapsed for good, with
  // the step chips absent from the DOM (VX-4749). Gating on the current count instead would go too far the other way,
  // forcing open a section that mounted multi-step with `defaultExpanded={false}`.
  useEffect(() => {
    if (hasAutoExpandedRef.current || hasUserToggledRef.current) return;
    if (hadMultipleStepsOnMountRef.current) return;
    if (steps != null && steps.length > 1) {
      setIsExpanded(true);
      hasAutoExpandedRef.current = true;
    }
  }, [steps]);

  const prevStepCountRef = useRef(steps?.length ?? 0);

  useEffect(() => {
    const container = stepsSectionRef.current;
    if (!container || currentStepIndex == null) return;

    const currentCount = steps?.length ?? 0;
    const wasAdded = currentCount > prevStepCountRef.current;
    prevStepCountRef.current = currentCount;

    if (wasAdded) {
      const addButton = container.querySelector<HTMLElement>(`[${ADD_STEP_BUTTON_ATTR}]`);
      addButton?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } else {
      const chip = container.querySelector<HTMLElement>(
        `[${STEP_CHIP_INDEX_ATTR}="${currentStepIndex}"]`
      );
      chip?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [currentStepIndex, steps?.length]);

  const handleContainerDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';

      const container = stepsSectionRef.current;
      if (!container || !steps?.length) return;

      const result = findClosestDragTarget(
        DragOrientation.Vertical,
        e,
        container,
        DRAG_TARGET_SELECTOR
      );
      if (result) {
        handleStepBarDragOver(result.index, result.position);
      }
    },
    [steps?.length, handleStepBarDragOver]
  );

  const handleContainerDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (dropTarget != null) {
        handleStepBarDrop(dropTarget.index);
      }
    },
    [dropTarget, handleStepBarDrop]
  );

  return (
    <>
      <HPDivider
        style={{
          width: '100%',
          marginBottom: '12px',
        }}
      />
      <CollapsibleHeader
        type="button"
        onClick={() => {
          hasUserToggledRef.current = true;
          setIsExpanded((expanded) => !expanded);
        }}
        data-testid="steps-section-toggle"
      >
        <CollapsibleTitle>Steps</CollapsibleTitle>
        <ChevronWrapper>{isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}</ChevronWrapper>
      </CollapsibleHeader>
      {isExpanded && (
        <StepsSection
          ref={stepsSectionRef}
          data-testid="steps-section"
          onDragOver={handleContainerDragOver}
          onDrop={handleContainerDrop}
        >
          {(steps ?? []).map((step, index) => {
            // Use step.smid when available, otherwise fall back to an index-based
            // identifier so rename/delete still work for steps without a backend smid
            // (e.g., steps from the /hang/ endpoint or newly created steps).
            const stepId = step.smid ?? generateTempStepId(index);
            const currentSteps = steps ?? [];

            return (
              <StepConfigButton
                key={stepId}
                stepName={step.display_name ?? `Step ${index + 1}`}
                active={index === currentStepIndex}
                isRenaming={renamingStepId === stepId}
                onClick={() => onStepClick(index)}
                onEdit={() => handleStartRename(stepId)}
                onDelete={(steps?.length ?? 0) > 1 ? () => deleteStep(stepId) : undefined}
                onRenameConfirm={(newName) => {
                  onDuplicateError?.(false);
                  handleRenameConfirm(stepId, newName);
                }}
                onRenameCancel={() => {
                  onDuplicateError?.(false);
                  handleRenameCancel();
                }}
                validateName={(name) => getStepNameError(name, currentSteps, stepId)}
                onNameChange={(name) => {
                  onDuplicateError?.(isProposedNameDuplicate(name, currentSteps, stepId));
                }}
                index={index}
                isDragging={draggedIndex === index}
                dropPosition={dropTarget?.index === index ? dropTarget.position : null}
                onDragStart={handleStepBarDragStart}
                onDragEnd={handleStepBarDragEnd}
              />
            );
          })}
          <AddStepButton
            variant={AddStepButtonVariant.Block}
            onClick={addStep}
            hoverText="Add a new step to the hanging protocol"
            keyboardShortcut="Shift + N"
            disabled={(steps?.length ?? 0) >= MAX_HP_STEPS || isSubmitting}
          />
        </StepsSection>
      )}
    </>
  );
};
